import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { UpdateUserMutation } from "src/api/User";
import { BorderButton } from "src/components/buttons/BorderButton";
import CancelButton from "src/components/buttons/CancelButton";
import { useForm } from "src/components/forms/Form";
import StepHeader from "src/components/forms/StepHeader";
import Container from "src/components/layout/Container";
import UserDepartments from "src/components/user/UserDepartments";
import UserModel from "src/models/UserModel";
import { AddUserFormValues } from "src/routes/admin/users/AddUser";
import { apiClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import helmet from "src/utils/helmet";
import message from "src/utils/message";
import { Level, OnboardType, updateUserVariables } from "types/code-generator";

interface Props {
  user: UserModel;
}

export default function EditDepartments(props: Props) {
  const form = useForm<AddUserFormValues>();
  const history = useHistory();
  const store = useStore();

  useEffect(() => {
    helmet.setPageTitle("Edit Permissions");
  }, []);

  function onSubmit() {
    form.validateFields().then((values) => {
      processForm(values);
    });
  }

  async function processForm(values) {
    const departments = Object.keys(values).filter((k) => values[k] === true);

    if (departments.length === 0) {
      message.error("Please select at least one department.");
      return;
    }

    console.log("values", values);

    const processingMessage: any = message.loading("Updating user…");
    form.setIsSubmitting(true);

    await updateUser(values)
      .then(() => {
        processingMessage();
        form.setIsSubmitting(false);
        message.success("User updated");

        if (
          props.user.level === Level.AccountOwner &&
          values.level !== Level.AccountOwner
        ) {
          history.push(`/admin/users/${props.user.id}/departments/edit`);
        } else {
          history.push(`/admin/users/${props.user.id}/more`);
        }
      })
      .catch((error) => {
        processingMessage();
        form.setIsSubmitting(false);
        message.error(error);
      });
  }

  function onCancel() {
    history.push(`/admin/users/${props.user.id}/more`);
  }

  function updateUser(values) {
    const departmentsToConnect = Object.keys(values)
      .filter((d) => values[d] === true && !d.includes("-onboard"))
      .map((d) => {
        return {
          id: d.replace("department-", ""),
        };
      });

    const onboardsToCreate = Object.keys(values)
      .filter((d) => values[d] === true && d.includes("-onboard"))
      .map((d) => {
        return {
          department: {
            connect: {
              id: d.replace("department-", "").replace("-onboard", ""),
            },
          },
          organization: {
            connect: {
              id: store.organization.id,
            },
          },
          type: OnboardType.Department,
        };
      });

    const variables: updateUserVariables = {
      data: {
        departments: {
          set: departmentsToConnect,
        },
        onboards: {
          create: onboardsToCreate,
        },
      },
      where: {
        id: props.user.id,
      },
    };

    return apiClient(UpdateUserMutation, {
      variables,
    });
  }

  return (
    <>
      <StepHeader>
        <p className="mb-2">
          <CancelButton onClick={() => history.goBack()} />
        </p>
        <p className="step-subhead mb-0">Department Access</p>
        <h2 className="mb-0 leading-none">
          {props.user.firstName} {props.user.lastName}
        </h2>
      </StepHeader>

      <div style={{ height: "120px" }} />

      <Container>
        <div>
          <UserDepartments form={form} user={props.user} />
          <div className="flex gap-4">
            <BorderButton type="submit" onClick={onSubmit}>
              Save
            </BorderButton>
            <BorderButton onClick={onCancel}>Cancel</BorderButton>
          </div>
        </div>
      </Container>
    </>
  );
}
